/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Column } from 'primereact/column';
import { PaginatorPageState } from 'primereact/paginator';
import { DataTable, DataTablePFSEvent, DataTableSortOrderType } from 'primereact/datatable';

import Filters from './filters/Filters';
import Pagination from '../common/pagination/Pagination';
import { NotificationLogResponse } from '../../models/notificationLogs/NotificationLogResponse';
import { NotificationLogFilter, KeyValuePair } from '../../models/notificationLogs/NotificationLogFilter';
import { DropdownOption } from '../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../services/httpService';
import { INotifierService, NotifierService } from '../../services/notifierService';
import { nameof } from '../../shared/utility';
import styles from './NotificationLogs.module.scss';

const NotificationLogs = (): JSX.Element => {
	const [loading, setLoading] = useState<boolean>(false);
	const [first, setFirst] = useState<number>(0);
	const [rows, setRows] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortField, setSortField] = useState<string>('');
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>();

	const truncateText = (text: string, maxLength: number): string => {
		return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
	};
	
	const [firstNameFilter, setFirstNameFilter] = useState<string>('');
	const [lastNameFilter, setLastNameFilter] = useState<string>('');
	// Lazy-initialize string filters directly from the URL so the very first API
	// call already carries the correct values (no race with the mount effect).
	const [emailFilter, setEmailFilter] = useState<string>(
		() => new URLSearchParams(window.location.search).get('email') ?? ''
	);
	const [divisionFilter, setDivisionFilter] = useState<string | number>('');
	const [yearFilter, setYearFilter] = useState<number | null>(null);
	const [quarterFilter, setQuarterFilter] = useState<number | null>(null);
	const [statusFilter, setStatusFilter] = useState<number | null>(null);
	const [emailSubjectFilter, setEmailSubjectFilter] = useState<string>(
		() => new URLSearchParams(window.location.search).get('emailsubject') ?? ''
	);

	const isUrlInitialized = useRef<boolean>(false);
	const urlRawParams = useRef<{
		y?: string;
		q?: string;
		status?: string;
		division?: string;
	}>({});

	const [divisionsOptions, setDivisionsOptions] = useState<DropdownOption[]>([]);
	const [yearsOptions, setYearsOptions] = useState<DropdownOption[]>([]);
	const [quartersOptions, setQuartersOptions] = useState<DropdownOption[]>([]);
	const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);

	const [notificationLogs, setNotificationLogs] = useState<NotificationLogResponse[]>([]);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(() => new NotifierService(), []);

	const location = useLocation();
	const navigate = useNavigate();

	// Debounce timers
	const debounceTimerRef = React.useRef<NodeJS.Timeout>();

	const fetchNotificationLogs = useCallback(() => {
		setLoading(true);

		let order = '';
		if (sortOrder) {
			order = sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
		}

		const params = new URLSearchParams({
			page: currentPage.toString(),
			size: rows.toString(),
			...(firstNameFilter && { firstName: firstNameFilter }),
			...(lastNameFilter && { lastName: lastNameFilter }),
			...(emailFilter && { email: emailFilter }),
			...(divisionFilter && { division: divisionFilter.toString() }),
			...(yearFilter && { year: yearFilter.toString() }),
			...(quarterFilter && { quarter: quarterFilter.toString() }),
			...(statusFilter && { status: statusFilter.toString() }),
			...(emailSubjectFilter && { emailSubject: emailSubjectFilter }),
			...(sortField && { sortBy: sortField }),
			...(order && { order: order })
		});

		httpService
			.get<NotificationLogResponse[]>(
				`api/notificationLog?${params.toString()}`,
				undefined
			)
			.then((response) => {
				// Client-side filter for emailSubject (not supported by API)
				const filtered = emailSubjectFilter
					? response.filter(log =>
						log.emailSubject?.toLowerCase().includes(emailSubjectFilter.toLowerCase())
					)
					: response;
				setNotificationLogs(filtered);
				setTotalRecords(filtered.length);
				setLoading(false);
			})
			.catch((err) => {
				notifierService.error();
				setLoading(false);
			});
	}, [
		currentPage,
		rows,
		//firstNameFilter,
		//lastNameFilter,
		emailFilter,
		divisionFilter,
		yearFilter,
		quarterFilter,
		statusFilter,
		emailSubjectFilter,
		sortField,
		sortOrder,
		httpService,
		notifierService
	]);

	// Initial load and filter changes
	useEffect(() => {
		setCurrentPage(1);
		setFirst(0);
	}, [
		//firstNameFilter,
		//lastNameFilter,
		 emailFilter,
		 divisionFilter,
		 yearFilter,
		 quarterFilter,
		 statusFilter,
		 emailSubjectFilter
		]);

	useEffect(() => {
		fetchNotificationLogs();
	}, [
		currentPage,
		rows,
		//firstNameFilter,
		//lastNameFilter,
		emailFilter,
		divisionFilter,
		yearFilter,
		quarterFilter,
		statusFilter,
		emailSubjectFilter,
		sortField,
		sortOrder
	]);

	// Parse URL query params on mount — store raw strings for options-dependent
	// params (year, quarter, division, status) that need to wait for options to load.
	// email and emailSubject are already initialized via lazy useState above.
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		urlRawParams.current = {
			...(searchParams.get('y') && { y: searchParams.get('y')! }),
			...(searchParams.get('q') && { q: searchParams.get('q')! }),
			...(searchParams.get('status') && { status: searchParams.get('status')! }),
			...(searchParams.get('division') && { division: searchParams.get('division')! }),
		};
	}, []);

	// Fetch filter options
	useEffect(() => {
		httpService
			.get<NotificationLogFilter>('api/notificationLog/filters', undefined)
			.then((filters) => {
				// Build option arrays locally so we can use them immediately for URL param resolution
				const divOpts: DropdownOption[] = filters.divisions
					? filters.divisions.map((d: string) => ({ label: d, value: d }))
					: [];
				const yearOpts: DropdownOption[] = filters.years
					? filters.years.map((y: number) => ({ label: y.toString(), value: y }))
					: [];
				const quarterOpts: DropdownOption[] = filters.quarters
					? filters.quarters.map((q: KeyValuePair<number, string>) => ({ label: q.value, value: q.key }))
					: [];
				const statusOpts: DropdownOption[] = filters.status
					? filters.status.map((s: KeyValuePair<number, string>) => ({ label: s.value, value: s.key }))
					: [];

				setDivisionsOptions(divOpts);
				setYearsOptions(yearOpts);
				setQuartersOptions(quarterOpts);
				setStatusOptions(statusOpts);

				// Resolve URL raw params to filter values now that options are available
				const raw = urlRawParams.current;
				if (raw.division) {
					const match = divOpts.find(o => o.value === raw.division);
					if (match) setDivisionFilter(match.value as string);
				}
				if (raw.y) {
					const yearNum = parseInt(raw.y, 10);
					const match = yearOpts.find(o => o.value === yearNum);
					if (match) setYearFilter(match.value as number);
				}
				if (raw.q) {
					// q=q1 → strip leading 'q'/'Q', parse → 1 → match by numeric key
					const quarterNum = parseInt(raw.q.replace(/^q/i, ''), 10);
					const match = quarterOpts.find(o => o.value === quarterNum);
					if (match) setQuarterFilter(match.value as number);
				}
				if (raw.status) {
					// status=failed → case-insensitive label match → numeric key
					const match = statusOpts.find(o =>
						o.label.toLowerCase() === raw.status!.toLowerCase()
					);
					if (match) setStatusFilter(match.value as number);
				}

				isUrlInitialized.current = true;
			})
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	// Keep URL in sync with filter state changes (two-way sync).
	// statusOptions is needed to convert numeric statusFilter back to a label string.
	useEffect(() => {
		if (!isUrlInitialized.current) return;
		const query = new URLSearchParams();
		if (emailFilter) query.set('email', emailFilter);
		if (yearFilter != null) query.set('y', yearFilter.toString());
		if (quarterFilter != null) query.set('q', `q${quarterFilter}`);
		if (statusFilter != null) {
			const statusLabel = statusOptions.find(o => o.value === statusFilter)?.label;
			if (statusLabel) query.set('status', statusLabel.toLowerCase());
		}
		if (divisionFilter) query.set('division', divisionFilter.toString());
		if (emailSubjectFilter) query.set('emailsubject', emailSubjectFilter);
		const qs = query.toString();
		navigate({ search: qs ? `?${qs}` : '' }, { replace: true });
	}, [emailFilter, divisionFilter, yearFilter, quarterFilter, statusFilter, emailSubjectFilter, statusOptions]);

	const handleFirstNameChange = (value: string) => {
		setFirstNameFilter(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
	};

	const handleLastNameChange = (value: string) => {
		setLastNameFilter(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
	};

	const handleEmailChange = (value: string) => {
		setEmailFilter(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
	};

	const onPageChange = (event: PaginatorPageState) => {
		setFirst(event.first);
		setRows(event.rows);
		setCurrentPage(event.page! + 1);
	};

	const onSort = (event: DataTablePFSEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	const onRenderSentOn = useCallback(
		(cellData: NotificationLogResponse) => {
			if (!cellData.sentOn) {
				return <span>-</span>;
			}
			try {
				const utcDate = new Date(cellData.sentOn);
				const localDate = new Date(utcDate.toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }));
				const formattedDate = localDate.toLocaleString('en-US', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false
				});
				return <span>{formattedDate}</span>;
			} catch (error) {
				return <span>{cellData.sentOn?.toString() ?? '-'}</span>;
			}
		},
		[]
	);

	return (
		<>
			<Filters
				divisionsOptions={divisionsOptions}
				yearsOptions={yearsOptions}
				quartersOptions={quartersOptions}
				statusOptions={statusOptions}
				firstNameFilter={firstNameFilter}
				setFirstNameFilter={handleFirstNameChange}
				lastNameFilter={lastNameFilter}
				setLastNameFilter={handleLastNameChange}
				emailFilter={emailFilter}
				setEmailFilter={handleEmailChange}
				divisionFilter={divisionFilter}
				setDivisionFilter={setDivisionFilter}
				yearFilter={yearFilter}
				setYearFilter={setYearFilter}
				quarterFilter={quarterFilter}
				setQuarterFilter={setQuarterFilter}
				statusFilter={statusFilter}
				setStatusFilter={setStatusFilter}
			/>

		<DataTable
			value={notificationLogs}
			responsiveLayout="scroll"
			sortField={sortField}
			sortOrder={sortOrder}
			onSort={onSort}
			loading={loading}
			resizableColumns
			columnResizeMode="expand"
		>
			{/* <Column
				sortable
				header="First Name"
				field={nameof<NotificationLogResponse>('firstName')}
				style={{ width: 'fit-content' }}
			/>

			<Column
				sortable
				header="Last Name"
				field={nameof<NotificationLogResponse>('lastName')}
				style={{ width: 'fit-content' }}
			/> */}

			<Column
				sortable
				header="Email"
				field={nameof<NotificationLogResponse>('email')}
				style={{ width: '100%' }}
			/>

			<Column
				sortable
				header="Division"
				field={nameof<NotificationLogResponse>('division')}
				style={{ width: 'fit-content' }}
			/>

			<Column
				sortable
				header="Year"
				field={nameof<NotificationLogResponse>('year')}
				style={{ width: 'fit-content' }}
			/>

			<Column
				sortable
				header="Quarter"
				field={nameof<NotificationLogResponse>('quarter')}
			/>

			<Column
				sortable
				header="Status"
				field={nameof<NotificationLogResponse>('status')}
				style={{ width: 'fit-content' }}
			/>

			<Column
				sortable
				header="Email Subject"
				field={nameof<NotificationLogResponse>('emailSubject')}
				style={{ width: '100%' }}
				body={(rowData: NotificationLogResponse) => (
					<span title={rowData.emailSubject}>
						{rowData.emailSubject}
					</span>
				)}
			/>

			<Column
				sortable
				header="Sent On (Local Time)"
				field={nameof<NotificationLogResponse>('sentOn')}
				style={{ width: 'fit-content' }}
				body={onRenderSentOn}
			/>
		</DataTable>			<Pagination
				totalRecords={totalRecords}
				rows={rows}
				first={first}
				onPageChange={onPageChange}
			/>
		</>
	);
};

export default NotificationLogs;